import moment = require("moment");
import React = require("react");
import DatePicker from "react-datepicker";
import {I18n} from "../components/Utils/i18n";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import "./filters.scss";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../index";

declare var AppTranslations: AppTranslations;
declare var LOCALE: string;

const comparators = ["-1", ">", "<", "=", ">=", "<="];

interface ValueOption {
    value: string;
    text: string;
    isEmpty?: boolean;
}

interface FilterProps {
    valueTitleSelect: string;
    defaultValueOption: string;
    valueOptions: ValueOption[];
    defaultComparisonOption: string;
    onChange: (value: string, comparison: string) => void;
}

interface FilterState {
    currentComparisonValue: string;
    currentValueOptionValue: string;
}

export class ComparativeValueFilter extends React.Component<FilterProps, FilterState> {

    public static stockLevelFilter(currentComparator: string,
                                   currentValue: string,
                                   onChange: (value: string, comparator: string) => void) {
        const stockLevelTranslations = AppTranslations.filters.statuses.stockLevel;
        return (
            <ComparativeValueFilter
                valueTitleSelect={stockLevelTranslations.title}
                defaultValueOption={currentValue}
                valueOptions={ComparativeValueFilter.getStockLevelPercentages()}
                defaultComparisonOption={currentComparator}
                onChange={onChange}
            />
        );
    }

    public static simpleComparableFilter(title: string,
                                         currentComparator: string,
                                         currentValue: string,
                                         onChange: (value: string, comparator: string) => void) {
        return (
            <ComparativeValueFilter
                valueTitleSelect={title}
                defaultValueOption={currentValue}
                valueOptions={ComparativeValueFilter.getStockLevelPercentages()}
                defaultComparisonOption={currentComparator}
                onChange={onChange}
            />
        );
    }

    private static getStockLevelPercentages() {
        return [
            {
                value: "0",
                text: "0%",
            },
            {
                value: "25",
                text: "25%",
            },
            {
                value: "35",
                text: "35%",
            },
            {
                value: "50",
                text: "50%",
            },
            {
                value: "75",
                text: "75%",
            },
            {
                value: "100",
                text: "100%",
            },
        ];
    }

    constructor(props: FilterProps) {
        super(props);
        this.onComparisonChange = this.onComparisonChange.bind(this);
        this.onValueOptionChange = this.onValueOptionChange.bind(this);
        let comparisonValue = "-1";
        if (this.props.defaultComparisonOption != null) {
            comparisonValue = this.props.defaultComparisonOption;
        }
        this.state = {
            currentValueOptionValue: this.props.defaultValueOption,
            currentComparisonValue: comparisonValue,
        };
    }

    public componentDidMount(): void {
        if (this.props.defaultComparisonOption != null && this.props.defaultComparisonOption !== "-1") {
            this.props.onChange(
                this.state.currentValueOptionValue,
                this.state.currentComparisonValue,
            );
        }
    }

    public reset() {
        this.setState(
            {
                currentValueOptionValue: null,
                currentComparisonValue: "-1",
            },
        );
        this.props.onChange(null, null);
    }

    public resetOps() {
        this.setState(
            {
                currentValueOptionValue: "0",
                currentComparisonValue: ">=",
            },
        );
    }

    public render() {
        if (this.props.valueOptions == null || this.props.valueOptions.length === 0) {
            return null;
        }
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div
                    className={"comparative-filter"}
                >
                    {this.renderComparisonSelect()}
                    {this.renderValueOptionSelect()}
                </div>
            </ThemeProvider>
        );
    }

    private onComparisonChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = e.target.value;
        let currentValue = this.state.currentValueOptionValue;
        if (this.state.currentValueOptionValue == null) {
            currentValue = "50";
        }
        this.setState({
            currentComparisonValue: option,
            currentValueOptionValue: currentValue,
        });
        this.props.onChange(
            currentValue,
            option === "-1" ? null : option,
        );
    }

    private onValueOptionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = this.findValueOption(e.target.value);
        this.setState({currentValueOptionValue: option.value});
        this.props.onChange(
            option.value,
            this.state.currentComparisonValue,
        );
    }

    private renderComparisonSelect() {
        const options: JSX.Element[] = [];
        for (let i = 0; i < comparators.length; i++) {
            options.push(
                (
                    <option value={comparators[i]} key={i}>
                        {comparators[i] === "-1" ? "" : comparators[i]}
                    </option>
                ),
            );
        }
        let selected = this.state.currentComparisonValue;
        if (selected == null) {
            selected = this.props.defaultComparisonOption;
        }
        let classNames = "filterTitleMaterial comparativeFilterTitle";
        const option = this.state.currentComparisonValue;
        if (option !== "-1") {
            classNames += " filtersGroupTitleEnlight";
        }
        return (
            <React.Fragment>
                <p className={classNames}>{this.props.valueTitleSelect}</p>
                <select
                    className={this.shouldHideValue() ? "groupFilterSelect filterContentSelect" : "groupFilterSelect filterSelectComparative"}
                    onChange={this.onComparisonChange}
                    value={selected}
                >
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private shouldHideValue(): boolean {
        return this.state.currentComparisonValue == null || this.state.currentComparisonValue === "-1";
    }

    private renderValueOptionSelect() {
        if (this.shouldHideValue()) {
            return null;
        }
        const options: JSX.Element[] = [];
        for (let i = 0; i < this.props.valueOptions.length; i++) {
            options.push(
                (
                    <option value={this.props.valueOptions[i].value} key={i}>
                        {this.props.valueOptions[i].text}
                    </option>
                ),
            );
        }
        let selected = this.state.currentValueOptionValue;
        if (selected == null) {
            selected = this.props.defaultValueOption;
        }
        return (
            <React.Fragment>
                <select
                    className="groupFilterSelect filterSelectNumber"
                    onChange={this.onValueOptionChange}
                    value={selected}
                >
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private findValueOption(value: string): ValueOption {
        if (value === null || this.props.valueOptions === null || this.props.valueOptions.length === 0) {
            return null;
        }
        for (const option of this.props.valueOptions) {
            if (option.value === value) {
                return option;
            }
        }
        return null;
    }
}
